import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormInput } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Strict domain shape: label-dot-label-…-dot-TLD where labels accept only
// letters/digits/hyphens (RFC-1035 character class) and the TLD is at least
// two letters. URLs, schemes, paths, ports, and any other punctuation are
// rejected so `https://example.com`, `example.com/path`, and
// `example.com:8080` don't quietly land in the domain bucket — the
// exclusion list is meant to mirror the email-recipient domain part only.
// Composed (not source-string interpolated) so callers see the regex
// literally and `EMAIL_RE` reuses the exact same domain body — drift here
// would silently re-permit URL-shaped inputs in the email bucket.
const DOMAIN_BODY =
  '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\\.[A-Za-z]{2,}';
const DOMAIN_RE = new RegExp(`^${DOMAIN_BODY}$`);
// The email right-hand side is the same domain shape; the local part is the
// practical RFC-5321 subset admins paste (letters/digits and `._%+-`). This
// rejects schemes (`mailto:alice@example.com`), paths
// (`alice@example.com/route`), and ports (`alice@example.com:8080`) — those
// were silently accepted by the prior `/^[^@\s]+@[^@\s]+$/` shape and would
// then be submitted as exclusion rows the backend cannot match against.
const EMAIL_RE = new RegExp(`^[A-Za-z0-9._%+-]+@${DOMAIN_BODY}$`);

// Tokenizer for the exclusions input. Accepts whitespace- or comma-separated
// values (FR-004 permits both). Strategy: strip an optional leading `@`
// (treating it as a typo prefix the admin may have copied from a handle),
// then classify the residual. Anything that still contains `@` must be a
// well-formed email or it's flagged invalid — this prevents `@alice@gmail.com`
// from silently bucketing as a domain. Output is lowercased, trimmed,
// deduplicated per bucket.
export const parseExclusionsInput = (raw) => {
  const tokens = String(raw || '')
    .split(/[\s,]+/)
    .map((t) => t.toLowerCase().trim())
    .filter((t) => t.length > 0);

  const emails = [];
  const domains = [];
  const invalid = [];

  tokens.forEach((token) => {
    const residual = token.replace(/^@/, '');
    if (residual.length === 0) {
      invalid.push(token);
      return;
    }

    if (residual.includes('@')) {
      if (EMAIL_RE.test(residual)) emails.push(residual);
      else invalid.push(token);
      return;
    }

    if (DOMAIN_RE.test(residual)) domains.push(residual);
    else invalid.push(token);
  });

  return {
    emails: Array.from(new Set(emails)),
    domains: Array.from(new Set(domains)),
    invalid: Array.from(new Set(invalid)),
  };
};

const TRIGGER_SX = {
  alignItems: 'center',
  gap: '4px',
  alignSelf: 'flex-start',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'text.main',
  font: 'inherit',
  textDecoration: 'underline',
  '&:disabled': { cursor: 'not-allowed', opacity: 0.6, textDecoration: 'none' },
};

const FIELD_NAME = 'gwi-exclusions-input';

export const ExclusionsField = ({ value, onChange, onValidityChange, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const parsed = useMemo(() => parseExclusionsInput(value), [value]);

  const toggle = useCallback(() => {
    if (disabled) return;
    setExpanded((prev) => !prev);
  }, [disabled]);

  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
      onValidityChange?.(parseExclusionsInput(e.target.value));
    },
    [onChange, onValidityChange],
  );

  return (
    <Flex flexDirection="column" sx={{ gap: '4px' }}>
      <Flex
        as="button"
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-expanded={expanded}
        aria-controls="gwi-exclusions-input"
        sx={TRIGGER_SX}
      >
        <Text fontSize="14px" lineHeight="1.4">
          Add exclusions
        </Text>
        <Box as={expanded ? ChevronUp : ChevronDown} size="16px" aria-hidden="true" />
      </Flex>

      {expanded && (
        <FormInput
          label="Enter emails and domains to exclude:"
          placeholder="Ex: example.com, doctor@gmail.com..."
          disabled={disabled}
          field={{
            id: FIELD_NAME,
            name: FIELD_NAME,
            value,
            onChange: handleChange,
          }}
          form={{
            errors: {
              [FIELD_NAME]: parsed.invalid.length > 0 ? `We couldn't parse: ${parsed.invalid.join(', ')}` : undefined,
            },
            touched: { [FIELD_NAME]: parsed.invalid.length > 0 },
          }}
        />
      )}
    </Flex>
  );
};

ExclusionsField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ExclusionsField.defaultProps = {
  value: '',
  onValidityChange: undefined,
  disabled: false,
};
